import React from 'react';
import __isEmpty from 'lodash/isEmpty';
import __forEach from 'lodash/forEach';

import AudioIcon from '../__assets/img/audio-icon.png';

export const createTableRowValue = (rowData = [], forceShow = false) => {
	/**
	 * rowData: [{
	 *  rowObj: {ts: number, value: string},
	 *  measurement: string,
	 *  shouldShow?: boolean
	 * }]
	 */

	return rowData.reduce((previousVal, currentVal, index) => {
		const { rowObj, units, show, key, log } = currentVal;
		log && console.log(log); // debugging only when needed

		const isDataEmpty =
			__isEmpty(rowObj) ||
			__isEmpty(rowObj?.value) ||
			// (key === 'cloudCoverage' && rowObj.value.replace(/ /g, '') === ',,,,,,')
			(key === 'lowestCloud' && rowObj?.value === '0');
		if (isDataEmpty) {
			if (index + 1 === rowData.length) {
				// Always return &nbsp; for empty values to maintain row height alignment
				// Previously returned null which could cause visual collapse in flexbox layouts
				return <>&nbsp;</>;
			}

			// another unit comes after
			return '';
		}

		if (!show) {
			return previousVal;
		}

		// add parentheses to units after the first unit
		let first = rowData[0];
		let didFirstShow = first.rowObj && first.rowObj.value && first.show;
		let val = index > 0 && didFirstShow ? `(${rowObj.value}${units})` : `${rowObj.value}${units}`;
		val = index > 0 ? ` ${val}` : val;
		return previousVal + val;
	}, '');
};

export const isTampered = (entry, data1HourAgo) => {
	// don't compare to prev if it has images
	return (
		!__isEmpty(data1HourAgo) &&
		!data1HourAgo.pict &&
		!data1HourAgo.pict1 &&
		((entry.lat && data1HourAgo.lat && entry.lat.value - data1HourAgo.lat.value > 0.005) ||
			(entry.lon && data1HourAgo.lon && entry.lon.value - data1HourAgo.lon.value > 0.005) ||
			(entry.elevation &&
				data1HourAgo.elevation &&
				entry.elevation.value - data1HourAgo.elevation.value > 3) ||
			(entry.orientation &&
				data1HourAgo.orientation &&
				entry.orientation.value - data1HourAgo.orientation.value > 10))
	);
};

// METAR weather codes with intensity prefixes
// Prefix: (-) = Light, (none) = Moderate, (+) = Heavy
const WEATHER_CODES = {
	// Rain
	'-RA': 'Light Rain',
	'RA': 'Moderate Rain',
	'+RA': 'Heavy Rain',
	// Drizzle
	'-DZ': 'Light Drizzle',
	'DZ': 'Moderate Drizzle',
	'+DZ': 'Heavy Drizzle',
	// Snow
	'-SN': 'Light Snow',
	'SN': 'Moderate Snow',
	'+SN': 'Heavy Snow',
	// Freezing
	'-FZRA': 'Light Freezing Rain',
	'FZRA': 'Freezing Rain',
	'+FZRA': 'Heavy Freezing Rain',
	'-FZDZ': 'Light Freezing Drizzle',
	'FZDZ': 'Freezing Drizzle',
	// Mixed
	'-RASN': 'Light Rain/Snow',
	'RASN': 'Rain/Snow Mix',
	'+RASN': 'Heavy Rain/Snow',
	// Thunderstorm
	'TS': 'Thunderstorm',
	'TSRA': 'Thunderstorm with Rain',
	'+TSRA': 'Heavy Thunderstorm with Rain',
	'TSSN': 'Thunderstorm with Snow',
	'TSGS': 'Thunderstorm with Small Hail',
	'TSGR': 'Thunderstorm with Hail',
	// Hail
	'GS': 'Small Hail',
	'GR': 'Hail',
	// Obscuration
	'FG': 'Fog',
	'FZFG': 'Freezing Fog',
	'BR': 'Mist',
	'HZ': 'Haze',
	// Unknown
	'UP': 'Unknown Precipitation',
};

// WMO Present Weather Codes — IWOS supported codes ONLY
// Source: LIT/IWOS_updated_04_2026/IWOS-DEC-FORMAT.numbers (PW field notes)
const WMO_WEATHER_CODES = {
	// No precipitation
	'0': 'None',
	'00': 'None',
	// Unknown
	'40': 'Unknown Precipitation',
	// Drizzle (50-59)
	'51': 'Light Drizzle',
	'52': 'Moderate Drizzle',
	'53': 'Heavy Drizzle',
	'56': 'Light Freezing Drizzle',
	'57': 'Freezing Drizzle',
	// Rain (60-69)
	'61': 'Light Rain',
	'62': 'Moderate Rain',
	'63': 'Heavy Rain',
	'65': 'Freezing Rain',
	// Snow (70-79)
	'71': 'Light Snow',
	'72': 'Moderate Snow',
	'73': 'Heavy Snow',
	// Hail
	'89': 'Hail',
	// Thunderstorms (90-99)
	'90': 'Thunderstorm',
	'92': 'Thunderstorm with Rain/Snow',
	'93': 'Thunderstorm with Hail',
	'95': 'Thunderstorm with Heavy Rain/Snow',
	'96': 'Thunderstorm with Heavy Hail',
};

// WMO Obscuration Codes — IWOS supported: 00=None, 04=HZ, 10=BR, 30=FG, 31=FZFG
// Mapping used inline in getMETAR() as obscMap for METAR title insertion

export const getWeather = (obj) => {
	if (__isEmpty(obj) || __isEmpty(obj.value)) {
		return <>&nbsp;</>;
	}

	const code = obj.value.trim();

	// First check METAR codes (alphabetic)
	const metarDescription = WEATHER_CODES[code];
	if (metarDescription) {
		return `${code} (${metarDescription})`;
	}

	// Then check WMO codes (numeric)
	const wmoDescription = WMO_WEATHER_CODES[code];
	if (wmoDescription) {
		return wmoDescription;
	}

	return code;
};

// Map weather codes (METAR or WMO numeric) to icon activation states
export const getWeatherIconStates = (weatherCode) => {
	const defaultStates = {
		rain: false,
		drizzle: false,
		snow: false,
		haze: false,
		fog: false,
		hail: false,
		freezingRain: false,
	};

	if (!weatherCode) {
		return defaultStates;
	}

	const code = String(weatherCode).toUpperCase().trim();
	const numCode = parseInt(code, 10);

	// Description strings (e.g. "Moderate Drizzle", "Heavy Snow") contain spaces
	// Match by keyword to avoid false substring hits (e.g. "RA" in "MODERATE")
	if (code.includes(' ')) {
		return {
			rain: /\bRAIN\b/.test(code) && !/\bFREEZING RAIN\b/.test(code),
			drizzle: /\bDRIZZLE\b/.test(code),
			snow: /\bSNOW\b/.test(code),
			haze: /\bHAZE\b|\bSMOKE\b/.test(code),
			fog: /\bFOG\b|\bMIST\b/.test(code),
			hail: /\bHAIL\b/.test(code),
			freezingRain: /\bFREEZING RAIN\b|\bFREEZING DRIZZLE\b/.test(code),
		};
	}

	// Check if it's a METAR code (alphabetic or longer than 2 chars)
	const isMETAR = isNaN(numCode) || code.length > 2;

	if (isMETAR) {
		// METAR codes use string pattern matching
		return {
			rain: code.includes('RA') && !code.includes('FZRA'),
			drizzle: code.includes('DZ'),
			snow: code.includes('SN'),
			haze: code.includes('HZ'),
			fog: code.includes('FG'),
			hail: code.includes('GR') || code.includes('GS'),
			freezingRain: code.includes('FZRA') || code.includes('FZDZ'),
		};
	}

	// WMO numeric codes use range matching
	return {
		drizzle: numCode >= 50 && numCode <= 59,                    // 50-59: Drizzle
		rain: (numCode >= 60 && numCode <= 69) ||                   // 60-69: Rain
		      (numCode >= 80 && numCode <= 82),                     // 80-82: Rain showers
		snow: (numCode >= 70 && numCode <= 79) ||                   // 70-79: Snow
		      (numCode >= 85 && numCode <= 86),                     // 85-86: Snow showers
		haze: numCode === 5,                                        // 05: Haze
		fog: numCode >= 40 && numCode <= 49,                        // 40-49: Fog
		hail: (numCode >= 96 && numCode <= 99) ||                   // 96-99: Thunderstorm with hail
		      (numCode >= 89 && numCode <= 90),                     // 89-90: Hail
		freezingRain: (numCode >= 56 && numCode <= 57) ||           // 56-57: Freezing drizzle
		             (numCode >= 66 && numCode <= 67),              // 66-67: Freezing rain
	};
};

// WMO Pressure Tendency Codes (0-8)
const PRESSURE_TENDENCY_CODES = {
	'0': 'Increasing, then decreasing',
	'1': 'Increasing then steady',
	'2': 'Increasing steadily',
	'3': 'Decreasing/steady, then increasing',
	'4': 'Steady',
	'5': 'Decreasing, then increasing',
	'6': 'Decreasing, then steady',
	'7': 'Decreasing steadily',
	'8': 'Steady/increasing, then decreasing',
};

export const getPressureTendency = (obj) => {
	if (__isEmpty(obj) || __isEmpty(obj.value)) {
		return <>&nbsp;</>;
	}

	const code = String(obj.value).trim();
	const description = PRESSURE_TENDENCY_CODES[code];

	if (description) {
		return description;
	}

	return code;
};

export const setValueColor = (row, data1HourAgo, metric, value) => {
	let colorClass;

	switch (metric) {
		case 'mws-voltage':
			if (__isEmpty(value) || __isEmpty(value.value)) {
				colorClass = '';
			} else if (value.value >= 5) {
				colorClass = 'good-value';
			} else if (value.value >= 4.8 && value.value < 5) {
				colorClass = 'meh-value';
			} else {
				colorClass = 'bad-value';
			}
			break;
		case 'sediment':
			if (__isEmpty(value) || __isEmpty(value.value)) {
				colorClass = '';
			} else if (value.value === 'Interference') {
				colorClass = 'bad-value';
			} else {
				colorClass = 'good-value';
			}
			break;
		case 'tampering':
			if (isTampered(row, data1HourAgo)) {
				colorClass = 'bad-value';
			} else {
				colorClass = 'good-value';
			}
			break;
		case 'flood':
			if (value.value === 1) {
				colorClass = 'bad-value';
			}
			break;
		default:
			colorClass = '';
			break;
	}

	return colorClass;
};

export const formatCloudLayersValue = (cloudLayers) => {
	let value =
		__isEmpty(cloudLayers) || __isEmpty(cloudLayers.value) ? <>&nbsp;</> : cloudLayers.value;

		if (
		value === 'NCD' ||
		value === 'CLR' ||
		value === 'No Ceilometer' ||
		value === 'Ceilometer OFF' ||
		React.isValidElement(value)
	) {
		return value;
	}
	
	const separateLayers = value.split(',');
	const formatted = separateLayers.map((layer) => {
		return `${layer.trim()} ft`;
	});

	return formatted.join(', ');
};

// MWS-specific cloud coverage formatting (original behavior before Phase 1 IWOS changes)
// This preserves the original MWS behavior that was working before we rewrote
// formatCloudLayersCoverage for IWOS WMO format support
export const formatCloudLayersCoverageMWS = (cloudLayers, cloudCoverage) => {
	let value =
		__isEmpty(cloudCoverage) || __isEmpty(cloudCoverage.value) ? <>&nbsp;</> : cloudCoverage.value;

	if (React.isValidElement(value)) {
		return value;
	}

	let formatted = formatCloudLayersValue(cloudLayers);
	const seperateCoverage = value.split(',');
	while (seperateCoverage[0] === 'CLR') {
		seperateCoverage.shift();
	}

	formatted = formatted.split(',');
	if (formatted.length === seperateCoverage.length) {
		for (let i = 0; i < formatted.length; i++) {
			formatted[i] = formatted[i] + ` (${seperateCoverage[i]})`;
		}
	}

	return formatted.join(',');
};

// Convert oktas (0-8) to WMO contraction
const getWmoFromOktas = (oktas) => {
	const value = parseInt(oktas) || 0;
	if (value === 0) return 'CLR';
	if (value <= 2) return 'FEW';
	if (value <= 4) return 'SCT';
	if (value <= 7) return 'BKN';
	return 'OVC';
};

export const formatCloudLayersCoverage = (cloudLayers, cloudCoverage) => {
	// Check if cloudLayers is empty or invalid
	if (__isEmpty(cloudLayers) || __isEmpty(cloudLayers.value)) {
		return <>&nbsp;</>;
	}

	const layersValue = cloudLayers.value;

	// Handle special cases
	if (
		layersValue === 'NCD' ||
		layersValue === 'CLR' ||
		layersValue === 'NA' ||
		layersValue === 'No Ceilometer' ||
		layersValue === 'Ceilometer OFF'
	) {
		return layersValue;
	}

	// Parse heights and coverages
	const heights = layersValue.split(',').map(h => h.trim());
	const coverages = cloudCoverage?.value?.split(',').map(c => c.trim()) || [];

	// Filter out empty/zero layers and format as WMO
	const formatted = [];
	for (let i = 0; i < Math.min(heights.length, 6); i++) {
		const height = parseInt(heights[i]) || 0;
		const oktas = parseInt(coverages[i]) || 0;

		// Skip layers with no height or no coverage
		if (height === 0 && oktas === 0) continue;

		const contraction = getWmoFromOktas(oktas);

		// If all layers are clear, just return CLR
		if (contraction === 'CLR' && formatted.length === 0 && i === heights.length - 1) {
			return 'CLR';
		}

		// Skip CLR entries in the middle of the list
		if (contraction === 'CLR') continue;

		// Height is in meters, display as hundreds of meters for METAR format
		const heightInHundredsM = Math.round(height / 100);
		const heightFormatted = String(heightInHundredsM).padStart(3, '0');

		formatted.push(`${contraction}${heightFormatted}`);
	}

	// If no layers with coverage, return CLR
	if (formatted.length === 0) {
		return 'CLR';
	}

	return formatted.join(', ');
}

// Format IWOS cloud layers as METAR-style strings (e.g., "FEW020 SCT035 OVC049")
// Primary: extract cloud groups from METAR string (always correct)
// Fallback: pair cloudCoverage text with cloudLayers heights
export const formatIWOSCloudLayers = (cloudCoverage, cloudLayers, metar) => {
	if (__isEmpty(cloudCoverage) || __isEmpty(cloudCoverage.value)) {
		return <>&nbsp;</>;
	}

	// Handle special cloudLayers values
	const layersValue = cloudLayers?.value;
	if (layersValue === 'No Ceilometer' || layersValue === 'Ceilometer OFF') {
		return layersValue;
	}

	// Primary: extract cloud groups from the METAR string
	if (metar?.value) {
		const cloudGroups = metar.value.match(/(?:FEW|SCT|BKN|OVC|VV)\d{3}/g);
		if (cloudGroups && cloudGroups.length > 0) {
			// Convert METAR codes to human-readable: FEW020 → "FEW 2000ft"
			return cloudGroups.map(g => {
				const cov = g.match(/[A-Z]+/)[0];
				const hundreds = parseInt(g.match(/\d+/)[0], 10);
				return `${cov} ${hundreds * 100}ft`;
			}).join(', ');
		}
		// METAR exists but no cloud groups — check for CLR/SKC
		if (/\bCLR\b|\bSKC\b/.test(metar.value)) {
			return 'CLR';
		}
	}

	// Fallback: pair cloudCoverage text with cloudLayers heights
	const coverages = cloudCoverage.value.split(',').map(c => c.trim());
	const nonClear = coverages.filter(c => c !== 'CLR' && c !== '' && c !== ' ');

	if (nonClear.length === 0) {
		return 'CLR';
	}

	if (!layersValue || layersValue === 'NCD') {
		return nonClear.join(' ');
	}

	const heights = layersValue.split(',').map(h => parseInt(h.trim()) || 0).filter(h => h > 0);

	if (heights.length === nonClear.length) {
		return nonClear.map((cov, i) => {
			return `${cov} ${heights[i]}ft`;
		}).join(', ');
	}

	return nonClear.join(' ');
};

export const renderAudioLinks = (row) => {
	if (row.spectralINP || row.spectralMP3 || row.spectralWAV) {
		return (
			<div className="audio-block">
				{row.spectralINP && row.spectralINP.value && (
					<a
						target="_blank"
						rel="noopener noreferrer"
						className="download-link"
						href={`${process.env.REACT_APP_AUDIO_URL}/${row.spectralINP.value}`}>
						<img alt="" src={AudioIcon} />
						Download INP File
					</a>
				)}
				{row.spectralMP3 && row.spectralMP3.value && (
					<a
						target="_blank"
						rel="noopener noreferrer"
						className="download-link"
						href={`${process.env.REACT_APP_AUDIO_URL}/${row.spectralMP3.value}`}>
						<img alt="" src={AudioIcon} />
						Download MP3 File
					</a>
				)}
				{row.spectralWAV && row.spectralWAV.value && (
					<a
						target="_blank"
						rel="noopener noreferrer"
						className="download-link"
						href={`${process.env.REACT_APP_AUDIO_URL}/${row.spectralWAV.value}`}>
						<img alt="" src={AudioIcon} />
						Download WAV File
					</a>
				)}
			</div>
		);
	}

	return null;
};

export const getGraphData = (entries, metric) => {
	const points = [];

	__forEach(entries, (entry) => {
		if (entry[metric]) {
			points.push({
				x: entry[metric].ts,
				y: parseFloat(entry[metric].value),
			});
		}
	});

	return points;
};

export const getMaxGraphData = (points) => {

	var max = -Infinity;

	__forEach(points, (point) => {
		if (point["y"] > max) {
			max = point["y"];
		}
	});

	return max;
};

export const getMinGraphData = (points) => {
	var min = Infinity;

	__forEach(points, (point) => {
		if (point["y"] < min) {
			min = point["y"];
		}
	});

	return min;
};

export const getMETAR = (data, showMetricOnly) => {
	let metar;
	if (!showMetricOnly) {
		metar = data.METAR ? data.METAR.value : 'No METAR';
	} else {
		metar = data.METARInternational
			? data.METARInternational.value
			: data.METAR
			? data.METAR.value
			: 'No METAR';
	}

	// Replace SLPNO with computed SLP from telemetry if available
	// METAR format: SLPppp = last 3 digits of tenths of hPa (e.g., 1013.2 → SLP132)
	// Fallback chain: SLPMb → slp (same hPa, different key) → SLPInHg (convert to hPa)
	if (metar.includes('SLPNO')) {
		let slpHpa = NaN;
		if (data.SLPMb && data.SLPMb.value) {
			slpHpa = parseFloat(data.SLPMb.value);
		} else if (data.slp && data.slp.value) {
			slpHpa = parseFloat(data.slp.value);
		} else if (data.SLPInHg && data.SLPInHg.value) {
			slpHpa = parseFloat(data.SLPInHg.value) * 33.8639;
		}
		if (!isNaN(slpHpa)) {
			const slpTenths = Math.round(slpHpa * 10);
			const slpStr = String(slpTenths).slice(-3);
			metar = metar.replace('SLPNO', `SLP${slpStr}`);
		}
	}

	// US domestic METAR uses CLR, not NCD (ICAO term).
	// Detect domestic format by presence of SM (statute miles) in the string.
	if (/\d+SM\b/.test(metar)) {
		metar = metar.replace(/\bNCD\b/, 'CLR');
	}

	// Remove coordinates from METAR string — not part of standard format.
	// METAR.js appends "lat° lon°" at the end; strip it.
	metar = metar.replace(/\s+-?\d+\.\d+°\s+-?\d+\.\d+°\s*$/, '');

	// Remove CLD LYR BLO — not in xlsx spec.
	metar = metar.replace(/CLD LYR BLO \d{3}\s*/g, '');

	// Fix weather intensity format: METAR.js outputs "RA-" but standard is "-RA".
	// Intensity prefix (-, +) goes BEFORE weather type per xlsx spec.
	metar = metar.replace(/\b(RA|DZ|SN|FZRA|FZDZ)([-+])/g, '$2$1');

	// Combine TS + precipitation into single group per xlsx spec:
	// "If precipitation is present report thunderstorm and precipitation"
	// TS -RA → -TSRA, TS RA → TSRA, TS +RA → +TSRA, same for SN, GR
	metar = metar.replace(/\bTS\s+([-+]?)(RA|SN|GR)\b/g, '$1TS$2');
	// Also handle VCTS with precipitation (VCTS -RA → VCTS stays separate per spec)
	// VCTS is vicinity — precipitation is reported separately

	// Fix PK WND: METAR.js outputs "MAX WND dddffKT" but xlsx spec says
	// "PK WND dddff/(hh)mm" — only when instantaneous wind > 25kt.
	metar = metar.replace(/\bMAX WND (\d{3})(\d{2,3})KT\s*/g, (match, dir, spd) => {
		const peakSpeed = parseInt(spd, 10);
		if (peakSpeed <= 25) return ''; // per xlsx: only when >25kt
		const tsMatch = metar.match(/\d{2}(\d{2})(\d{2})Z/);
		if (tsMatch) return `PK WND ${dir}${spd}/${tsMatch[1]}${tsMatch[2]} `;
		return `PK WND ${dir}${spd} `;
	});

	// Add missing obscuration to Present Weather in body.
	// METAR.js only outputs precipitation weather code, not obscuration.
	// Obscuration value may be WMO code (04,10,30,31) or METAR abbreviation (HZ,BR,FG,FZFG).
	const wmoToObsc = { '04': 'HZ', '10': 'BR', '30': 'FG', '31': 'FZFG' };
	const validObsc = ['HZ', 'BR', 'FG', 'FZFG'];
	if (data.obscuration && data.obscuration.value) {
		const raw = data.obscuration.value.trim();
		const obscMetar = wmoToObsc[raw] || (validObsc.includes(raw) ? raw : null);
		if (obscMetar && !metar.includes(obscMetar)) {
			// Insert obscuration before sky condition (FEW/SCT/BKN/OVC/VV/CLR/NCD)
			metar = metar.replace(
				/(\s)(FEW|SCT|BKN|OVC|VV\d|CLR|NCD)/,
				` ${obscMetar} $2`
			);
		}
	}

	// Append missing remarks from telemetry data.
	// These are not generated by METAR.js but the data exists in the packet.

	// (8) Hourly Precipitation: Prrrr — amount in hundredths of inch
	// Per xlsx: "Omit the group if no precipitation occurred since the last METAR"
	if (data.precipAcc1hr && data.precipAcc1hr.value) {
		const precipIn = parseFloat(data.precipAcc1hr.value);
		if (!isNaN(precipIn) && precipIn > 0 && !metar.includes('P0')) {
			const precipHundredths = Math.round(precipIn * 100);
			metar = metar.replace(/\s*$/, ` P${String(precipHundredths).padStart(4, '0')}`);
		}
	}

	// (11) Hourly Temp/Dewpoint: TsnTTTsnTdTdTd — tenths of °C
	if (data.temperatureC && data.temperatureC.value) {
		const tempC = parseFloat(data.temperatureC.value);
		if (!isNaN(tempC) && !/\bT\d{8}\b/.test(metar)) {
			const tSign = tempC < 0 ? '1' : '0';
			const tVal = String(Math.round(Math.abs(tempC) * 10)).padStart(3, '0');
			let tempRmk = `T${tSign}${tVal}`;
			if (data.dewPoint && data.dewPoint.value) {
				const dpC = parseFloat(data.dewPoint.value);
				if (!isNaN(dpC)) {
					const dSign = dpC < 0 ? '1' : '0';
					const dVal = String(Math.round(Math.abs(dpC) * 10)).padStart(3, '0');
					tempRmk += `${dSign}${dVal}`;
				}
			}
			metar = metar.replace(/\s*$/, ` ${tempRmk}`);
		}
	}

	// (15) Pressure Tendency: 5appp — only at 00/03/06/09/12/15/18/21Z per xlsx
	// threeHourTendency = tendency code (a), pressureTendency3hr = differential in mb
	const tsHourMatch = metar.match(/\d{2}(\d{2})\d{2}Z/);
	const obsHour = tsHourMatch ? parseInt(tsHourMatch[1], 10) : -1;
	const tendencyHours = [0, 3, 6, 9, 12, 15, 18, 21];
	if (tendencyHours.includes(obsHour) && data.threeHourTendency && data.threeHourTendency.value && !/\b5\d{4}\b/.test(metar)) {
		const tendencyCode = data.threeHourTendency.value.trim();
		if (/^[0-8]$/.test(tendencyCode)) {
			let ppp = '000';
			if (data.pressureTendency3hr && data.pressureTendency3hr.value) {
				const diffMb = Math.abs(parseFloat(data.pressureTendency3hr.value));
				if (!isNaN(diffMb)) {
					ppp = String(Math.round(diffMb * 10)).padStart(3, '0');
				}
			}
			metar = metar.replace(/\s*$/, ` 5${tendencyCode}${ppp}`);
		}
	}

	return metar;
};

//if the row has a No METAR header, return false
export const showRow = (data, showMetricOnly) => {
	if (!showMetricOnly) {
		return data.METAR ? data.METAR.value : false; 
	}

	return data.METARInternational
		? data.METARInternational.value
		: true
		? data.METAR.value
		: false;
};

export const doesWeatherValueExist = (weatherVal) => {
	return weatherVal && !__isEmpty(weatherVal.value);
};
